import { useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowSquareOut, FilePlus, MapPin, Plus, Trash, X as XIcon } from '@phosphor-icons/react';
import { api } from '@/lib/api';
import {
  DISTRESS_FLAGS,
  FLAG_LABELS,
  STATUS_LABELS,
  type MailingListItem,
  type OwnerWithContacts,
  type PropertyDetail as Detail,
  type PropertyListItem,
  type PropertyPhoto,
  type PropertyStatus,
} from '@/lib/types';
import { cn } from '@/lib/utils';
import GenerateLetterPanel from '@/components/GenerateLetterPanel';
import KatastrUploadModal from '@/components/KatastrUploadModal';

function mapyUrl(lat: number, lon: number): string {
  return `https://mapy.com/letecka?source=coor&id=${lon}%2C${lat}&x=${lon}&y=${lat}&z=19`;
}

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);

  const query = useQuery({
    queryKey: ['property', id],
    queryFn: () => api<Detail>(`/api/properties/${id}`),
    enabled: !!id,
  });

  const del = useMutation({
    mutationFn: () => api(`/api/properties/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['properties'] });
      navigate('/properties');
    },
  });

  const mailingsQuery = useQuery({
    queryKey: ['mailings', id],
    queryFn: () =>
      api<{ ok: true; items: MailingListItem[]; count: number }>(
        `/api/mailings?propertyId=${id}`,
      ),
    enabled: !!id,
  });

  if (query.isLoading) return <p className="text-sm text-muted-foreground">Načítám…</p>;
  if (query.error)
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
        Nepodařilo se načíst: {String(query.error)}
      </div>
    );
  if (!query.data) return null;

  const { property, owners, photos } = query.data;
  const { flags } = property;

  const isUnclassified = property.status === 'unclassified';

  return (
    <div className="space-y-6 max-w-4xl">
      <header className="space-y-2">
        <div className="text-xs text-muted-foreground">
          <Link to="/properties" className="hover:underline">
            Nemovitosti
          </Link>
          {' / '}
          <span>{property.parcela || property.id.slice(0, 8)}</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {isUnclassified
            ? 'Nezatříděná nemovitost'
            : property.ulice && property.cp
              ? `${property.ulice} ${property.cp}`
              : property.obec || property.id}
        </h1>
        <div className="text-sm text-muted-foreground">
          {property.obec}
          {property.ku && property.ku !== property.obec ? ` · k. ú. ${property.ku}` : ''}
          {property.parcela ? ` · parcela ${property.parcela}` : ''}
          {property.lv ? ` · LV ${property.lv}` : ''}
        </div>
      </header>

      {isUnclassified && <UnclassifiedPanel property={property} />}

      {!isUnclassified && <StatusActions property={property} />}

      {flags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {flags.map((f) => (
            <span
              key={f}
              className={cn(
                'px-2.5 py-1 rounded-md text-xs font-medium border',
                DISTRESS_FLAGS.has(f)
                  ? 'bg-destructive/10 border-destructive/40 text-destructive'
                  : 'bg-amber-100 border-amber-300 text-amber-900',
              )}
            >
              {FLAG_LABELS[f] || f}
            </span>
          ))}
        </div>
      )}

      {!isUnclassified && (
        <PhotoGallery propertyId={property.id} photos={photos} primaryFileId={property.photoFileId} />
      )}

      <section className="rounded-lg border">
        <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium">
          Údaje o nemovitosti
        </div>
        <dl className="grid grid-cols-[160px_1fr] gap-x-4 gap-y-1.5 p-4 text-sm">
          <DT label="Obec" value={property.obec} />
          <DT label="Katastrální území" value={property.ku} />
          <DT label="Parcela" value={property.parcela} />
          <DT label="Č. p." value={property.cp} />
          <DT label="Ulice" value={property.ulice} />
          <DT label="LV" value={property.lv} />
          <DT label="Typ" value={property.typ} />
        </dl>
      </section>

      <section className="rounded-lg border">
        <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium flex items-baseline justify-between">
          <span>Vlastníci a kontakty</span>
          <span className="text-xs text-muted-foreground font-normal">{owners.length} osob/subjektů</span>
        </div>
        <ul className="divide-y">
          {owners.map((o) => (
            <OwnerCard key={o.id} owner={o} />
          ))}
          {owners.length === 0 && (
            <li className="p-4 text-sm text-muted-foreground">Žádný vlastník.</li>
          )}
        </ul>
      </section>

      <section className="rounded-lg border">
        <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium flex items-center justify-between">
          <span>Historie dopisů</span>
          {!showGenerate && (
            <button
              onClick={() => setShowGenerate(true)}
              className="px-3 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium"
            >
              + Vygenerovat dopis
            </button>
          )}
        </div>

        {showGenerate && (
          <div className="p-4">
            <GenerateLetterPanel
              propertyId={property.id}
              owners={owners}
              onClose={() => setShowGenerate(false)}
            />
          </div>
        )}

        <MailingsList items={mailingsQuery.data?.items || []} loading={mailingsQuery.isLoading} propertyId={property.id} />
      </section>

      <details className="rounded-lg border">
        <summary className="px-4 py-2.5 cursor-pointer text-sm font-medium select-none">
          Debug — dedup klíč a další
        </summary>
        <div className="px-4 pb-4 text-xs space-y-1 font-mono text-muted-foreground">
          <div>id: {property.id}</div>
          <div>dedupKey: {property.dedupKey}</div>
          <div>created: {new Date(property.createdAt).toLocaleString('cs-CZ')}</div>
          <div>updated: {new Date(property.updatedAt).toLocaleString('cs-CZ')}</div>
        </div>
      </details>

      <div className="flex items-center gap-3 pt-2 border-t">
        <button
          onClick={() => navigate('/properties')}
          className="px-4 py-2 rounded-md border text-sm hover:bg-muted"
        >
          Zpět na seznam
        </button>
        <div className="ml-auto flex items-center gap-2">
          {confirmDelete && (
            <span className="text-xs text-destructive">
              Včetně {owners.length} vlastníků a jejich kontaktů. Opravdu?
            </span>
          )}
          {confirmDelete && (
            <button
              onClick={() => setConfirmDelete(false)}
              disabled={del.isPending}
              className="px-3 py-2 rounded-md border text-sm hover:bg-muted"
            >
              Zrušit
            </button>
          )}
          <button
            onClick={() => (confirmDelete ? del.mutate() : setConfirmDelete(true))}
            disabled={del.isPending}
            className={cn(
              'px-4 py-2 rounded-md text-sm font-medium disabled:opacity-50',
              confirmDelete
                ? 'bg-destructive text-destructive-foreground'
                : 'border border-destructive text-destructive hover:bg-destructive/10',
            )}
          >
            {del.isPending ? 'Mažu…' : confirmDelete ? 'Ano, smazat' : 'Smazat'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Posun mezi stavy — kromě 'unclassified', do kterého se nevrací (vzniká jen
// uploadem fotky). 'parcel_resolved' je interní mezi-stav (přijde s reverse-geo
// integrací) — ručně ho nevolíme. V UI tedy nabízíme katastr_loaded / ready /
// archived; libovolný směr (i archiv → ready, reaktivace).
const STATUS_BUTTONS: PropertyStatus[] = ['katastr_loaded', 'ready', 'archived'];

function StatusActions({ property }: { property: PropertyListItem }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (status: PropertyStatus) =>
      api(`/api/properties/${property.id}`, { method: 'PATCH', json: { status } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property', property.id] });
      qc.invalidateQueries({ queryKey: ['properties'] });
    },
  });

  return (
    <section className="rounded-lg border p-3 flex items-center gap-3 text-sm flex-wrap">
      <span className="text-muted-foreground text-xs uppercase tracking-wide">Stav</span>
      <span className="font-medium">{STATUS_LABELS[property.status]}</span>
      <div className="ml-auto flex flex-wrap gap-2">
        {STATUS_BUTTONS.filter((s) => s !== property.status).map((s) => (
          <button
            key={s}
            onClick={() => mut.mutate(s)}
            disabled={mut.isPending}
            className="px-2.5 py-1 rounded-md border text-xs hover:bg-muted disabled:opacity-50"
          >
            → {STATUS_LABELS[s]}
          </button>
        ))}
      </div>
      {mut.error && (
        <span className="text-xs text-destructive">Chyba: {String(mut.error)}</span>
      )}
    </section>
  );
}

function PhotoGallery({
  propertyId,
  photos,
  primaryFileId,
}: {
  propertyId: string;
  photos: PropertyPhoto[];
  primaryFileId: string | null;
}) {
  const qc = useQueryClient();
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState<{ x: number; y: number } | null>(null);
  const [confirmDelId, setConfirmDelId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (files: FileList) => {
      const form = new FormData();
      for (const f of Array.from(files)) form.append('files', f);
      const res = await fetch(`/api/properties/${propertyId}/photos`, { method: 'POST', body: form });
      const data = await res.json();
      if (!res.ok) throw new Error((data as { message?: string })?.message || `HTTP ${res.status}`);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['property', propertyId] }),
  });

  const del = useMutation({
    mutationFn: (fileId: string) =>
      api(`/api/properties/${propertyId}/photos/${fileId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['property', propertyId] }),
  });

  if (photos.length === 0) return null;

  return (
    <section className="rounded-lg border">
      <div className="px-4 py-2.5 border-b bg-muted/40 text-sm font-medium flex items-center justify-between">
        <span>Fotky <span className="text-muted-foreground font-normal">({photos.length})</span></span>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={upload.isPending}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs hover:bg-muted disabled:opacity-50"
        >
          <Plus size={11} />
          {upload.isPending ? 'Nahrávám…' : 'Přidat'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/heic,image/heif,image/png"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) upload.mutate(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      <div className="p-3 flex flex-wrap gap-2">
        {photos.map((ph) => {
          const isPrimary = ph.fileId === primaryFileId;
          return (
            <div
              key={ph.fileId}
              className="relative group"
              onMouseEnter={(e) => {
                setHoverId(ph.fileId);
                setHoverPos({ x: e.clientX, y: e.clientY });
              }}
              onMouseMove={(e) => setHoverPos({ x: e.clientX, y: e.clientY })}
              onMouseLeave={() => {
                setHoverId(null);
                setHoverPos(null);
              }}
            >
              <img
                src={`/api/photos/${ph.fileId}`}
                alt=""
                loading="lazy"
                className="w-20 h-20 object-cover rounded border bg-muted cursor-zoom-in"
              />
              {isPrimary && (
                <span className="absolute bottom-0 left-0 text-[9px] px-1 rounded-tr rounded-bl bg-foreground/80 text-background">
                  GPS
                </span>
              )}
              {!isPrimary && confirmDelId !== ph.fileId && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDelId(ph.fileId);
                  }}
                  className="absolute -top-1 -right-1 hidden group-hover:flex items-center justify-center w-5 h-5 rounded-full bg-destructive text-destructive-foreground shadow text-xs"
                  title="Smazat"
                >
                  <XIcon size={11} />
                </button>
              )}
              {confirmDelId === ph.fileId && (
                <div className="absolute inset-0 rounded border bg-background/95 flex flex-col items-center justify-center gap-1 text-[10px]">
                  <span className="font-medium">Smazat?</span>
                  <div className="flex gap-1">
                    <button
                      onClick={() => {
                        del.mutate(ph.fileId);
                        setConfirmDelId(null);
                      }}
                      className="px-1.5 py-0.5 rounded bg-destructive text-destructive-foreground"
                    >
                      Ano
                    </button>
                    <button
                      onClick={() => setConfirmDelId(null)}
                      className="px-1.5 py-0.5 rounded border"
                    >
                      Ne
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {hoverId && hoverPos && (
        <HoverPreview fileId={hoverId} x={hoverPos.x} y={hoverPos.y} />
      )}
      {upload.error && (
        <div className="px-4 pb-3 text-xs text-destructive">Chyba: {String(upload.error)}</div>
      )}
    </section>
  );
}

function HoverPreview({ fileId, x, y }: { fileId: string; x: number; y: number }) {
  // Plovoucí preview pozicovaný napravo nahoře od kurzoru. Pokud by se nevešel
  // do viewportu (vpravo / dole), překlopíme stranu.
  const W = 480;
  const H = 360;
  const margin = 16;
  let left = x + 20;
  let top = y - H / 2;
  if (left + W + margin > window.innerWidth) left = x - W - 20;
  if (top < margin) top = margin;
  if (top + H + margin > window.innerHeight) top = window.innerHeight - H - margin;
  return (
    <div
      className="fixed z-40 pointer-events-none border rounded-lg shadow-xl bg-background overflow-hidden"
      style={{ left, top, width: W, height: H }}
    >
      <img
        src={`/api/photos/${fileId}`}
        alt=""
        className="w-full h-full object-contain bg-muted"
      />
    </div>
  );
}

function UnclassifiedPanel({ property }: { property: PropertyListItem }) {
  const [uploadOpen, setUploadOpen] = useState(false);
  const hasGps = property.gpsLat != null && property.gpsLon != null;

  return (
    <section className="rounded-lg border bg-amber-50/40 p-4 flex flex-wrap items-center gap-4">
      {property.photoFileId ? (
        <img
          src={`/api/photos/${property.photoFileId}`}
          alt="fotka z terénu"
          className="w-24 h-24 object-cover rounded border shrink-0"
        />
      ) : (
        <div className="w-24 h-24 rounded border bg-muted/40 flex items-center justify-center text-xs text-muted-foreground shrink-0">
          bez fotky
        </div>
      )}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {hasGps && (
          <span className="inline-flex items-center gap-1 text-muted-foreground">
            <MapPin size={13} />
            <span className="font-mono tabular-nums">
              {property.gpsLat!.toFixed(5)}, {property.gpsLon!.toFixed(5)}
            </span>
          </span>
        )}
        {hasGps && (
          <a
            href={mapyUrl(property.gpsLat!, property.gpsLon!)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-0.5 text-primary hover:underline"
          >
            mapy.com
            <ArrowSquareOut size={11} />
          </a>
        )}
        <button
          onClick={() => setUploadOpen(true)}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-primary text-primary-foreground text-xs font-medium"
        >
          <FilePlus size={12} />
          Vložit katastr
        </button>
      </div>
      {uploadOpen && (
        <KatastrUploadModal
          propertyId={property.id}
          title={hasGps ? `${property.gpsLat!.toFixed(5)}, ${property.gpsLon!.toFixed(5)}` : undefined}
          onClose={() => setUploadOpen(false)}
        />
      )}
    </section>
  );
}

function DT({ label, value }: { label: string; value: string | null }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={value ? '' : 'text-muted-foreground italic'}>{value || '—'}</dd>
    </>
  );
}

function OwnerCard({ owner: o }: { owner: OwnerWithContacts }) {
  return (
    <li className="p-4 space-y-2">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium flex items-center gap-2 flex-wrap">
            {o.name}
            {o.isCompany && (
              <Badge color="purple" text="právnická" />
            )}
          </div>
          {o.address && (
            <div className="text-xs text-muted-foreground mt-0.5">{o.address}</div>
          )}
        </div>
        <div className="font-mono text-sm tabular-nums shrink-0">{o.share || '—'}</div>
      </div>
      {o.contacts.length > 0 && (
        <div className="ml-0 pl-3 border-l-2 border-muted text-xs space-y-1">
          {o.contacts.map((c) => (
            <div key={c.id} className="flex items-center gap-2">
              <span className="text-muted-foreground uppercase text-[10px] tracking-wide w-12">
                {c.channel}
              </span>
              <span className={c.optOut ? 'line-through text-muted-foreground' : ''}>{c.value}</span>
              {c.source && (
                <span className="text-[10px] text-muted-foreground">({c.source})</span>
              )}
              {c.optOut && <span className="text-destructive text-[10px]">OPT-OUT</span>}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

function MailingsList({
  items,
  loading,
  propertyId,
}: {
  items: MailingListItem[];
  loading: boolean;
  propertyId: string;
}) {
  if (loading) {
    return <div className="p-4 text-sm text-muted-foreground">Načítám…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="p-4 text-sm text-muted-foreground italic">
        Zatím nebyly vygenerované žádné dopisy.
      </div>
    );
  }
  return (
    <ul className="divide-y">
      {items.map(({ mailing, template, owner }) => (
        <MailingRow key={mailing.id} mailing={mailing} template={template} owner={owner} propertyId={propertyId} />
      ))}
    </ul>
  );
}

function MailingRow({
  mailing,
  template,
  owner,
  propertyId,
}: MailingListItem & { propertyId: string }) {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const del = useMutation({
    mutationFn: () => api(`/api/mailings/${mailing.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mailings', propertyId] }),
  });
  return (
    <li className="p-3 flex items-center gap-3 text-sm">
      <StatusBadge status={mailing.status} />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate">
          {template.name}
          <span className="text-muted-foreground font-normal"> → {owner.name}</span>
        </div>
        <div className="text-xs text-muted-foreground">
          {new Date(mailing.queuedAt).toLocaleString('cs-CZ')}
        </div>
      </div>
      {mailing.pdfFileId && (
        <a
          href={`/api/mailings/${mailing.id}/pdf`}
          target="_blank"
          rel="noreferrer"
          className="px-2.5 py-1 rounded border text-xs hover:bg-muted"
        >
          PDF
        </a>
      )}
      {confirming ? (
        <>
          <button
            onClick={() => del.mutate()}
            disabled={del.isPending}
            className="px-2 py-1 rounded bg-destructive text-destructive-foreground text-xs font-medium disabled:opacity-50"
          >
            Smazat
          </button>
          <button
            onClick={() => setConfirming(false)}
            className="px-2 py-1 rounded border text-xs hover:bg-muted"
          >
            Zrušit
          </button>
        </>
      ) : (
        <button
          onClick={() => setConfirming(true)}
          className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive"
          title="Smazat dopis"
        >
          <Trash size={14} />
        </button>
      )}
    </li>
  );
}

function StatusBadge({ status }: { status: string }) {
  const palette: Record<string, string> = {
    queued: 'bg-amber-100 text-amber-900 border-amber-200',
    printed: 'bg-blue-100 text-blue-900 border-blue-200',
    posted: 'bg-emerald-100 text-emerald-900 border-emerald-200',
    returned: 'bg-destructive/10 text-destructive border-destructive/40',
    acknowledged: 'bg-slate-100 text-slate-700 border-slate-200',
  };
  const labels: Record<string, string> = {
    queued: 've frontě',
    printed: 'vytištěno',
    posted: 'odesláno',
    returned: 'vráceno',
    acknowledged: 'reakce',
  };
  return (
    <span
      className={cn(
        'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border shrink-0',
        palette[status] || 'bg-muted text-muted-foreground border-border',
      )}
    >
      {labels[status] || status}
    </span>
  );
}

function Badge({ color, text }: { color: 'purple' | 'blue' | 'slate'; text: string }) {
  const palette = {
    purple: 'bg-purple-100 text-purple-900 border-purple-200',
    blue: 'bg-blue-100 text-blue-900 border-blue-200',
    slate: 'bg-slate-100 text-slate-700 border-slate-200',
  }[color];
  return (
    <span
      className={cn(
        'text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border',
        palette,
      )}
    >
      {text}
    </span>
  );
}
